if (!customElements.get('turf-challenge-carousel')) {
  /**
   * Peek carousel. Native overflow + snap, with IFA-style button paging:
   * first card pins to 0, last page pins to maxScroll, and mobile cards
   * between those two edges center in the viewport.
   */
  class TurfChallengeCarousel extends HTMLElement {
    #abortController;
    #matchMedia;
    #viewportObserver;
    #isInViewport = false;
    #resizeFrame = 0;
    #scrollFrame = 0;
    #scrollAnimFrame = 0;
    #snapRestoreFrame = 0;
    #gsapWaitFrame = 0;
    #currentIndex = 0;
    #isAnimating = false;

    connectedCallback() {
      if (this.#abortController) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.viewport = this.querySelector('[data-challenge-viewport]');
      this.track = this.querySelector('[data-challenge-track]');
      this.previousButton = this.querySelector('[data-challenge-previous]');
      this.nextButton = this.querySelector('[data-challenge-next]');
      this.cards = Array.from(this.querySelectorAll('[data-challenge-card]'));
      this.slides = this.cards.map(
        (card) => card.closest('.shopify-block__turf-challenge-card') || card,
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.#bindStaticEvents(signal);
      this.#observeViewport(signal);
      this.#setupEntrance();
      this.#syncControls();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#teardown();
    }

    /**
     * Complete cards in view. Peek of the next card remains visible.
     * @returns {number}
     */
    #visibleCount() {
      if (window.matchMedia('(min-width: 1280px)').matches) return 2;
      return 1;
    }

    /**
     * Last index that still changes scroll position.
     * @returns {number}
     */
    #maxIndex() {
      if (!this.slides.length) return 0;
      return Math.max(0, this.slides.length - this.#visibleCount());
    }

    /**
     * Button animation duration from the UX breakpoint table.
     * @returns {number}
     */
    #scrollDuration() {
      if (window.matchMedia('(min-width: 1280px)').matches) return 450;
      if (window.matchMedia('(min-width: 768px)').matches) return 400;
      return 350;
    }

    /**
     * @param {AbortSignal} signal
     */
    #bindStaticEvents(signal) {
      this.previousButton?.addEventListener('click', () => this.#move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.#move(1), { signal });
      this.viewport?.addEventListener('scroll', () => this.#handleScroll(), { signal, passive: true });
      // Arrow keys page the carousel only while focus is inside it, so other widgets keep their arrow keys.
      this.addEventListener('keydown', this.#handleKeydown, { signal });
      window.addEventListener('resize', this.#handleResize, { signal, passive: true });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
      }
    }

    /**
     * One-card step with IFA targeting. Previous skips indexes that still
     * sit on the end rail so the left control never dead-clicks.
     * @param {number} direction
     */
    #move(direction) {
      if (this.#isAnimating) return;
      if (direction < 0 && this.previousButton?.disabled) return;
      if (direction > 0 && this.nextButton?.disabled) return;

      const maxIndex = this.#maxIndex();
      let nextIndex = Math.max(0, Math.min(maxIndex, this.#currentIndex + direction));

      if (direction < 0 && nextIndex > 0) {
        const currentTravel = this.#targetLeft(this.#currentIndex);
        while (nextIndex > 0 && Math.abs(this.#targetLeft(nextIndex) - currentTravel) <= 1) {
          nextIndex -= 1;
        }
      }

      if (nextIndex === this.#currentIndex) return;

      this.#scrollToIndex(nextIndex);
    }

    /**
     * @param {number} index
     * @returns {number}
     */
    #targetLeft(index) {
      const scroller = this.viewport;
      const slide = this.slides[index];
      if (!slide || !scroller) return 0;

      const isFinalPosition = index >= this.#maxIndex();
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

      if (index === 0) return 0;
      if (
        window.matchMedia('(max-width: 767px)').matches &&
        index > 0 &&
        !isFinalPosition
      ) {
        return this.#centeredSlideOffset(slide, scroller, maxScroll);
      }
      if (isFinalPosition) return maxScroll;
      return this.#slideOffset(slide, scroller);
    }

    /**
     * Targeting: first and last cards align to their outer gutters; mobile
     * cards between them center; tablet/desktop retain IFA alignment.
     * @param {number} index
     * @param {'smooth' | 'auto'} [behavior]
     */
    #scrollToIndex(index, behavior = 'smooth') {
      const scroller = this.viewport;
      const slide = this.slides[index];
      if (!slide || !scroller) return;

      this.#currentIndex = index;
      this.#syncControls();

      const targetLeft = this.#targetLeft(index);
      const distance = Math.abs(targetLeft - scroller.scrollLeft);
      const duration = Math.min(720, Math.max(this.#scrollDuration(), distance * 0.55));

      if (behavior === 'auto' || this.reducedMotion) {
        this.#stopTween();
        scroller.scrollLeft = targetLeft;
        this.#isAnimating = false;
        scroller.classList.remove('is-scrolling');
        return;
      }

      this.#animateScrollTo(scroller, targetLeft, duration);
    }

    /**
     * @param {HTMLElement} scroller
     * @param {number} targetLeft
     * @param {number} duration
     */
    #animateScrollTo(scroller, targetLeft, duration) {
      this.#stopTween();

      const startLeft = scroller.scrollLeft;
      const delta = targetLeft - startLeft;

      if (Math.abs(delta) < 1) {
        scroller.scrollLeft = targetLeft;
        return;
      }

      this.#isAnimating = true;
      scroller.classList.add('is-scrolling');

      const startTime = performance.now();

      const tick = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        const eased =
          progress < 0.5 ? 4 * progress * progress * progress : 1 - (-2 * progress + 2) ** 3 / 2;

        scroller.scrollLeft = startLeft + delta * eased;

        if (progress < 1) {
          this.#scrollAnimFrame = window.requestAnimationFrame(tick);
          return;
        }

        scroller.scrollLeft = targetLeft;
        this.#releaseScrollLock();
      };

      this.#scrollAnimFrame = window.requestAnimationFrame(tick);
    }

    #stopTween() {
      window.cancelAnimationFrame(this.#scrollAnimFrame);
      window.cancelAnimationFrame(this.#snapRestoreFrame);
      this.#scrollAnimFrame = 0;
      this.#snapRestoreFrame = 0;
    }

    /**
     * Re-enable snap after the final scrollLeft has painted. Restoring on the
     * same frame lets mandatory snap correct the first item by one gap.
     */
    #releaseScrollLock() {
      const scroller = this.viewport;
      this.#stopTween();

      this.#snapRestoreFrame = window.requestAnimationFrame(() => {
        this.#snapRestoreFrame = window.requestAnimationFrame(() => {
          this.#isAnimating = false;
          scroller?.classList.remove('is-scrolling');
          this.#snapRestoreFrame = 0;
        });
      });
    }

    #cancelProgrammaticScroll() {
      this.#stopTween();
      this.#isAnimating = false;
      this.viewport?.classList.remove('is-scrolling');
    }

    /**
     * Left-align a card to the content start (after scroll-padding).
     * @param {Element} slide
     * @param {HTMLElement} scroller
     * @returns {number}
     */
    #slideOffset(slide, scroller) {
      const scrollerRect = scroller.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const scrollPaddingStart =
        Number.parseFloat(window.getComputedStyle(scroller).scrollPaddingInlineStart) || 0;

      return Math.max(
        0,
        scroller.scrollLeft + slideRect.left - scrollerRect.left - scrollPaddingStart,
      );
    }

    /**
     * Center a card in the mobile viewport without exceeding the scroll rail.
     * @param {Element} slide
     * @param {HTMLElement} scroller
     * @param {number} maxScroll
     * @returns {number}
     */
    #centeredSlideOffset(slide, scroller, maxScroll) {
      const scrollerRect = scroller.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const centeredLeft =
        scroller.scrollLeft +
        slideRect.left -
        scrollerRect.left -
        (scroller.clientWidth - slideRect.width) / 2;

      return Math.max(0, Math.min(maxScroll, centeredLeft));
    }

    #handleScroll() {
      if (this.#isAnimating) return;

      window.cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = window.requestAnimationFrame(() => {
        const scroller = this.viewport;
        if (!scroller || !this.slides.length) return;

        const left = scroller.scrollLeft;
        const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

        if (left >= maxScroll - 1) {
          this.#currentIndex = this.#maxIndex();
          this.#syncControls();
          return;
        }

        let closestIndex = 0;
        let closestDistance = Infinity;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(this.#targetLeft(index) - left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.#currentIndex = Math.min(closestIndex, this.#maxIndex());
        this.#syncControls();
      });
    }

    #syncControls() {
      const maxIndex = this.#maxIndex();
      this.previousButton?.toggleAttribute('disabled', this.#currentIndex <= 0);
      this.nextButton?.toggleAttribute('disabled', this.#currentIndex >= maxIndex);
    }

    /**
     * @param {AbortSignal} signal
     */
    #observeViewport(signal) {
      if (!('IntersectionObserver' in window)) {
        this.#isInViewport = true;
        return;
      }

      this.#viewportObserver = new IntersectionObserver(
        ([entry]) => {
          this.#isInViewport = Boolean(entry?.isIntersecting);
        },
        { threshold: 0.35 },
      );
      this.#viewportObserver.observe(this);
      signal.addEventListener('abort', () => {
        this.#viewportObserver?.disconnect();
        this.#viewportObserver = null;
      });
    }

    #setupEntrance() {
      if (this.dataset.enableEntrance !== 'true' || window.Shopify?.designMode) return;
      if (this.reducedMotion) return;

      this.#whenGsapReady(() => {
        if (!this.isConnected || !window.gsap || !window.ScrollTrigger) return;

        window.gsap.registerPlugin(window.ScrollTrigger);
        this.#matchMedia = window.gsap.matchMedia();
        this.#matchMedia.add('(prefers-reduced-motion: no-preference)', () => {
          const heading = this.querySelector('[data-challenge-heading]');
          const slides = this.slides;
          const nav = this.querySelector('[data-challenge-nav]');

          if (heading) {
            window.gsap.set(heading, { autoAlpha: 0, y: 20 });
            window.gsap.to(heading, {
              autoAlpha: 1,
              y: 0,
              duration: 0.5,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: this,
                start: 'top 75%',
                once: true,
              },
            });
          }

          if (slides.length) {
            window.gsap.set(slides, { autoAlpha: 0, x: 30 });
            window.gsap.to(slides, {
              autoAlpha: 1,
              x: 0,
              duration: 0.5,
              ease: 'power3.out',
              stagger: 0.08,
              scrollTrigger: {
                trigger: this,
                start: 'top 65%',
                once: true,
              },
            });
          }

          if (nav) {
            window.gsap.set(nav, { autoAlpha: 0, y: 16 });
            window.gsap.to(nav, {
              autoAlpha: 1,
              y: 0,
              duration: 0.45,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: this,
                start: 'top 55%',
                once: true,
              },
            });
          }
        });
      });
    }

    /**
     * @param {() => void} callback
     */
    #whenGsapReady(callback) {
      const startedAt = performance.now();

      const tick = () => {
        if (!this.isConnected) return;
        if (window.gsap && window.ScrollTrigger) {
          callback();
          return;
        }
        if (performance.now() - startedAt > 4000) return;
        this.#gsapWaitFrame = window.requestAnimationFrame(tick);
      };

      tick();
    }

    #handleKeydown = (event) => {
      if (event.defaultPrevented || !this.#isInViewport) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      if (event.target instanceof HTMLSelectElement) return;
      if (event.target instanceof HTMLElement && event.target.isContentEditable) return;

      event.preventDefault();
      this.#move(event.key === 'ArrowRight' ? 1 : -1);
    };

    #handleResize = () => {
      window.cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = window.requestAnimationFrame(() => {
        const maxIndex = this.#maxIndex();
        this.#scrollToIndex(Math.min(this.#currentIndex, maxIndex), 'auto');
      });
    };

    #handleBlockSelect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;

      const slide = event.target.closest('.shopify-block__turf-challenge-card');
      if (!slide || !this.contains(slide)) return;

      const index = this.slides.indexOf(slide);
      if (index >= 0) this.#scrollToIndex(Math.min(index, this.#maxIndex()));
    };

    #teardown() {
      window.cancelAnimationFrame(this.#resizeFrame);
      window.cancelAnimationFrame(this.#scrollFrame);
      window.cancelAnimationFrame(this.#gsapWaitFrame);
      this.#cancelProgrammaticScroll();
      this.#viewportObserver?.disconnect();
      this.#viewportObserver = null;
      this.#matchMedia?.revert();
      this.#matchMedia = null;
    }
  }

  customElements.define('turf-challenge-carousel', TurfChallengeCarousel);
}
